import { useQueries, useQueryClient } from '@tanstack/react-query'
import { Table } from 'components/table'
import { ICount } from 'components/table/table-wrapper'
import WrapperWithHeader from 'components/wrapper-with-header'
import { ITableHeadDTO } from 'data/dtos/components/i-table-head-dto'
import { INotificationDTO, INotificationParams } from 'data/dtos/operation/i-notification-dto'
import React, { ChangeEvent, MouseEvent, useState } from 'react'
import api from 'services/api'
import { loadCount, loadList } from 'services/requests'
import { formatCNPJ } from 'utils/is-valid-cnpj'
import { useStyles } from './styles'

const headCells: ITableHeadDTO[] = [
  {
    id: 'title',
    label: 'Título',
    width: 4,
    type: 'text',
  },
  {
    id: 'cnpj',
    label: 'CNPJ',
    width: 3,
    type: 'text',
  },
  {
    id: 'body',
    label: 'Detalhe',
    width: 5,
    type: 'text',
  },
]

const NotificationList: React.FC = () => {
  const [page, setPage] = useState<number>(0)
  const [search, setSearch] = useState<string>('')
  const [rowsPerPage, setRowsPerPage] = useState<number>(100)
  const [columnOrder, setColumnOrder] = useState<('ASC' | 'DESC')[]>([])

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [recordToDelete, setRecordToDelete] = useState<string | null>('')

  const classes = useStyles()
  const queryClient = useQueryClient()

  const [list, count] = useQueries({
    queries: [
      {
        queryKey: ['notifications', 'list', search, page, rowsPerPage, columnOrder],
        queryFn: () => loadList('/notifications/list', { search, page, rowsPerPage, columnOrder, organizationId: '' }),
        initialData: { data: { data: [] } } as any,
      },
      {
        queryKey: ['notifications', 'count', search],
        queryFn: () => loadCount('/notifications/count', search),
        initialData: { data: { data: { count: 0 } } } as any,
      },
    ],
  })

  if (list.isError || count.isError) {
    return <div>Error...</div>
  }

  const rawList = (list.data?.data.data as INotificationDTO[]) ?? []
  const listData = rawList.map((n) => {
    let cnpj = '—'
    try {
      const p = n.params ? (JSON.parse(n.params) as INotificationParams) : {}
      if (p.federalTaxId) cnpj = formatCNPJ(p.federalTaxId, false)
    } catch {
      // params not JSON or malformed — leave cnpj as '—'
    }
    return { ...n, cnpj, _unread: !n.readAt }
  })
  const countData = count.data?.data.data as ICount

  const handleRowClick = async (row: INotificationDTO & { _unread: boolean }) => {
    if (!row._unread || !row.id) return
    try {
      await api.put('/notifications', { id: row.id, readAt: new Date().toISOString() })
      await Promise.all([list.refetch(), count.refetch()])
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    } catch (err) {
      console.error('Failed to mark as read', err)
    }
  }

  const handleDelete = async () => {
    await api
      .delete(`/notifications/${recordToDelete}`)
      .then(async () => await Promise.all([list.refetch(), count.refetch()]))
      .catch((error) => console.log(error.response?.data))
  }

  const searchChange = (value: string) => setSearch(value)

  const handleClose = () => setModalIsOpen(false)

  const handleChangePage = (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    event!.preventDefault()
    setPage(newPage)
  }

  const handleChangeRowsPerPage = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleCloseDelete = () => {
    setModalIsOpen(false)
    handleDelete()
  }

  const handleDeleteButton = (e: MouseEvent<any, any>, id: string) => {
    e.stopPropagation()
    setModalIsOpen(true)
    setRecordToDelete(id)
  }

  const handleSortChange = (index: number) => {
    columnOrder[index] = columnOrder[index] === 'ASC' ? 'DESC' : 'ASC'

    setColumnOrder(columnOrder)
  }

  const tabOptions = [
    {
      title: 'Todas',
      size: countData.count,
      handleClick: () => {},
    },
  ]

  return (
    <WrapperWithHeader>
      <Table.Root>
        <Table.Info
          title='Notificações'
          subTitle='Notificações da plataforma.'
        />

        <Table.tabs options={tabOptions} />
        <Table.SearchBar
          value={search}
          handleChange={searchChange}
        />

        <Table.Overlay
          isEmpty={listData.length <= 0}
          isSearch={search.length > 0}
          isLoading={list.isPending || count.isPending}
          route='/notifications/new'
          searchMessage='Nenhum notificação encontrada'
          emptyMessage='Seja bem-vindo à página de gerenciamento de Notificação. Até o momento, nenhum notificação foi criada'
        >
          <Table.Wrapper
            totalRows={countData.count}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            page={page}
            handleClose={handleClose}
            handleCloseDelete={handleCloseDelete}
            modalIsOpen={modalIsOpen}
          >
            <Table.Header
              handleSortChange={handleSortChange}
              setOrder={setColumnOrder}
              headCells={headCells}
              order={columnOrder}
            />
            <Table.Body
              handleDeleteButton={handleDeleteButton}
              headCells={headCells}
              rows={listData}
              onRowClick={handleRowClick}
              getRowStyle={(row) => ({ fontWeight: row._unread ? 600 : undefined })}
            />
          </Table.Wrapper>
        </Table.Overlay>
      </Table.Root>
    </WrapperWithHeader>
  )
}

export default NotificationList
